<template>
  <NuxtLayout
    v-if="campaign"
    :name="setLayout"
  >
    <div class="pb-12">
      <TranslationTool>
        <CopyField
          class="w-full"
          :text="campaignEndpoint"
        />
      </TranslationTool>
      <div
        v-if="temporaryBanner === 'lufthansa'"
        class="mt-6"
      >
        <LazyLufthansaBanner />
      </div>

      <div class="mt-8 md:py-2">
        <CampaignPreamble :campaign="campaign" />
      </div>

      <div class="mb-10 md:mb-14">
        <LazyCampaignVariantLastMinute
          v-if="campaignVariant === 'last-minute' || campaignVariant === 'airport'"
          :campaign="campaign"
        />
        <LazyCampaignVariantDefault
          v-else
          :campaign="campaign"
          :big-trip="cheapestTrip"
        />
      </div>

      <LazyCampaignReadMoreCategory
        v-if="campaignCategory"
        class="mb-10 md:mb-16"
        :campaign-category="campaignCategory"
      />

      <div
        v-if="reviewsSamples.length"
        class="mb-10 md:mb-16"
      >
        <h3
          class="font-extrabold mb-4"
          data-i18n="campaign.readmore-title"
        >
          {{ $t('campaign.readmore-title', { categoryName: campaignCategory?.name ?? $t('trips') }) }}
        </h3>
        <div class="flex flex-col w-full">
          <HydrateWhenVisible>
            <BlogWithContent
              :reviews-samples="reviewsSamples"
              :is-campaign-page="true"
            />
          </HydrateWhenVisible>
        </div>
      </div>

      <div>
        <h3
          class="font-extrabold mb-4"
          data-i18n="campaign.categories-title"
        >
          {{ $t('campaign.categories-title') }}
        </h3>
        <HydrateWhenVisible>
          <LazyCategoryBlocks />
        </HydrateWhenVisible>
      </div>
    </div>
  </NuxtLayout>
</template>

<script setup>
const localeStore = useLocaleStore()
const calendarStore = useCalendarStore()
const rootStore = useRootStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const { urls: localeURLs } = useLocale()

const { setPageBanner } = useBanner()

const { error, data } = await useAsyncData(async () => {
  try {
    const campaignEndpoint = `/${localeStore.locale}/campaign/${route.params.name}`

    const { data: campaign } = await apiFetch(campaignEndpoint)

    const campaignVariant = campaign.campaign_type

    const pageBanner = {
      background: {
        image: {
          url: campaign.hero_image_cloudinary,
        },
      },

      type: 'default',
      linkButton: campaign.call_to_action_text ? {
        text: campaign.call_to_action_text,
        href: 'campaignActionBtnScrollToElement',
        bgColor: 'blue',
      } : null,
    }
    calendarStore.SET_CAMPAIGN_ID(campaign?.id)
    calendarStore.SET_CAMPAIGN_VARIANT(campaignVariant)
    if (campaignVariant === 'last-minute' || campaignVariant === 'airport') {
      pageBanner.subtitleColor = 'orange'
      pageBanner.subtitleWeight = 'bold'

      if (campaign.origin_airports?.length) {
        calendarStore.SET_FORCE_AIRPORT(campaign.origin_airports[0])
      }

      await Promise.all([
        calendarStore.resetFilters(false),
        calendarStore.setFromBackupStateQuery(route.fullPath),
        calendarStore.getCalendarOptions(),
      ])
    }

    let campaignCategory
    if (campaign.trip_category !== null) {
      campaignCategory = rootStore.getCategoryById(campaign.trip_category)?.category
    }

    setPageBanner(pageBanner)

    localeStore.SET_PAGE_WIDE_TRANSLATIONS(
      createPageWideTranslation(
        ['calenderHomeDescription', 'siteTitleBrand'],
        ['topic', 'description', 'page'],
        campaign
      )
    )

    let cheapestTrip
    campaign?.triptypes.forEach((trip) => {
      if (!cheapestTrip || cheapestTrip.price_min > trip.price_min) {
        cheapestTrip = trip
      }
    })

    return {
      campaign,
      campaignVariant,
      cheapestTrip,
      campaignEndpoint,
      campaignCategory,
    }
  } catch (e) {
    throw new Error('Post not found: ' + router.currentRoute.value.fullPath)
  }
})

if (error.value) {
  throw createError({
    message: error.value.message,
    statusCode: 404,
    stack: error.value.stack,
    fatal: true,
  })
}

const {
  campaign,
  campaignVariant,
  campaignCategory,
  cheapestTrip,
  campaignEndpoint,
} = data.value

useHead({
  title: () => campaign.topic,
  meta: () => [
    {
      hid: 'og:url',
      property: 'og:url',
      content: () => `${localeURLs.BASE_URL}${localeURLs.campaign}/${route.params.name}`,
    },
    {
      hid: 'og:title',
      property: 'og:title',
      content: `${campaign.topic}`,
    },
    {
      hid: 'og:image',
      property: 'og:image',
      content: () => formatCloudinaryImagePath(cheapestTrip?.main_picture_cloudinary || campaign?.hero_image_cloudinary, {
        transformation: { watermark: true },
        forceJPG: true,
        isSol: false,
      }),
    },
    {
      hid: 'og:description',
      name: 'og:description',
      content: () => t('calenderHomeDescription'),
    },
    {
      hid: 'description',
      name: 'description',
      content: () => t('calenderHomeDescription'),
    },
  ],
})
const setLayout = computed(() => {
  return isSol() ? 'charter-page' : 'banner-default'
})

const temporaryBanner = computed(() => {
  const showBannerForCampaignId = {
    rolfsbuss: {
      // till-italien
      132: 'lufthansa',
    },

    solresor: {},
  }

  const bannersForLocale = showBannerForCampaignId[isSol() ? 'solresor' : 'rolfsbuss']
  return bannersForLocale[campaign.id]
})

const reviewsSamples = computed(() => (campaign.reviews_samples || []).slice(0, 2))

onBeforeUnmount(() => {
  calendarStore.SET_CAMPAIGN_ID(null)
  calendarStore.SET_CAMPAIGN_VARIANT(null)
  calendarStore.SET_FORCE_AIRPORT(null)
})
</script>

<style lang="scss">
@import "@layers/web/assets/scss/modules/product/_product-trip-result";
</style>
